/**
 * Session Protection Hook
 * Monitors sessionStorage tokens (AL-213) for tampering, deletion, or
 * corruption. Automatically redirects to login if tokens are compromised.
 */

import { useCallback, useEffect } from 'react';
import { decodeToken, getStoredTokens, signOut } from '../services/authService';
import { logger } from '../utils/logger';

interface SessionMonitorProps {
  isAuthenticated: boolean;
  onSessionInvalid: () => void;
  checkInterval?: number;
}

/**
 * Hook to monitor session integrity
 * Detects: token deletion, corruption, expiration, or tampering
 */
export const useSessionProtection = ({
  isAuthenticated,
  onSessionInvalid,
  checkInterval = 5000, // Check every 5 seconds
}: SessionMonitorProps) => {
  const validateTokenIntegrity = useCallback(() => {
    if (!isAuthenticated) {
      return true; // Not logged in, no session to protect
    }

    try {
      const tokens = getStoredTokens();

      // Token was deleted from storage
      if (!tokens) {
        logger.warn('❌ Session tokens deleted from storage');
        onSessionInvalid();
        return false;
      }

      // Validate token structure
      if (!tokens.idToken || !tokens.accessToken) {
        logger.warn('⚠️ Token structure invalid or incomplete');
        onSessionInvalid();
        return false;
      }

      // Check if tokens are corrupted (not valid JWT format)
      const decodedId = decodeToken(tokens.idToken);
      const decodedAccess = decodeToken(tokens.accessToken);

      if (!decodedId || !decodedAccess) {
        logger.warn('⚠️ Token decoding failed - possible corruption');
        onSessionInvalid();
        return false;
      }

      // Check if tokens have expired
      const now = Math.floor(Date.now() / 1000);
      if (decodedId.exp && decodedId.exp < now) {
        logger.warn('⏰ ID Token has expired');
        onSessionInvalid();
        return false;
      }

      if (decodedAccess.exp && decodedAccess.exp < now) {
        logger.warn('⏰ Access Token has expired');
        onSessionInvalid();
        return false;
      }

      logger.debug('✅ Session tokens validated successfully');
      return true;
    } catch (error) {
      logger.error('Error validating session integrity:', error);
      onSessionInvalid();
      return false;
    }
  }, [isAuthenticated, onSessionInvalid]);

  // Monitor for storage events. Note: sessionStorage (used for auth tokens
  // since AL-213) is tab-scoped, so this listener won't fire for token
  // changes in *other* tabs — each tab has its own isolated sessionStorage.
  // It's kept for same-tab defence in depth and in case other app code ever
  // stores related data in localStorage. Continuous interval-based
  // validation below is the primary tamper/deletion detection mechanism.
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (
        event.key === 'idToken' ||
        event.key === 'accessToken' ||
        event.key === 'refreshToken'
      ) {
        logger.warn('🚨 Auth token modified from another source');

        // If old value exists but new value is null, tokens were deleted
        if (event.oldValue && !event.newValue) {
          logger.error('🚨 Session tokens deleted - logging out');
          signOut();
          onSessionInvalid();
          return;
        }

        // If value changed unexpectedly, tokens were tampered with
        if (event.oldValue && event.newValue && event.oldValue !== event.newValue) {
          logger.error('🚨 Session tokens tampered with - logging out');
          signOut();
          onSessionInvalid();
          return;
        }
      }
    },
    [onSessionInvalid]
  );

  // Setup interval check and storage listener
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Set up periodic validation
    const intervalId = setInterval(() => {
      validateTokenIntegrity();
    }, checkInterval);

    // Listen for changes from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Initial validation
    validateTokenIntegrity();

    // Cleanup
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, validateTokenIntegrity, handleStorageChange, checkInterval]);

  return { validateTokenIntegrity };
};

/**
 * Hook to detect document visibility changes
 * Re-validates session when tab regains focus
 */
export const useSessionFocusCheck = ({
  isAuthenticated,
  onSessionInvalid,
}: Omit<SessionMonitorProps, 'checkInterval'>) => {
  const validateOnFocus = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    logger.debug('Tab regained focus - validating session...');
    const tokens = getStoredTokens();

    if (!tokens) {
      // Check isAuthenticated again as it might have just been cleared
      if (isAuthenticated) {
        logger.warn('❌ Session tokens missing - redirecting to login');
        onSessionInvalid();
      }
      return;
    }

    // Quick integrity check
    const decoded = decodeToken(tokens.idToken);
    if (!decoded) {
      logger.warn('⚠️ Token corrupted - redirecting to login');
      onSessionInvalid();
    }
  }, [isAuthenticated, onSessionInvalid]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        validateOnFocus();
      }
    });

    return () => {
      document.removeEventListener('visibilitychange', validateOnFocus);
    };
  }, [isAuthenticated, validateOnFocus]);

  return { validateOnFocus };
};

/**
 * Hook to monitor idle time and auto-logout
 */
export const useSessionIdleTimeout = ({
  isAuthenticated,
  onSessionInvalid,
  idleTimeout = 30 * 24 * 60 * 60 * 1000, // 30 days
}: SessionMonitorProps & { idleTimeout?: number }) => {
  // Idle timeout disabled — uncomment the block below to re-enable
  // useEffect(() => {
  //   if (!isAuthenticated) {
  //     return;
  //   }
  //
  //   let timeoutId: NodeJS.Timeout;
  //
  //   const resetIdleTimer = () => {
  //     clearTimeout(timeoutId);
  //
  //     timeoutId = setTimeout(() => {
  //       logger.warn('⏰ Session idle timeout - logging out');
  //       onSessionInvalid();
  //     }, idleTimeout);
  //   };
  //
  //   // Activity events to listen for
  //   const events = [
  //     'mousedown',
  //     'mousemove',
  //     'keypress',
  //     'scroll',
  //     'touchstart',
  //     'click',
  //   ];
  //
  //   events.forEach((event) => {
  //     document.addEventListener(event, resetIdleTimer, true);
  //   });
  //
  //   // Initial timer
  //   resetIdleTimer();
  //
  //   return () => {
  //     clearTimeout(timeoutId);
  //     events.forEach((event) => {
  //       document.removeEventListener(event, resetIdleTimer, true);
  //     });
  //   };
  // }, [isAuthenticated, onSessionInvalid, idleTimeout]);
};
